import { Link } from "@/lib/router-compat";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLanguage } from "@/hooks/useLanguage";
import { getServiceDetailT } from "@/i18n/serviceDetail";
import { localizeHref } from "@/lib/localizeHref";

export interface IndustryLinkItem {
  title: string;
  description: string;
  image: string;
  href: string;
}

export interface LinkGroup {
  heading: string;
  links: IndustryLinkItem[];
}

interface Props {
  links: IndustryLinkItem[];
  groupHeading?: string;
  extraGroups?: LinkGroup[];
  sectionTitle?: string;
  sectionSubtitle?: string;
  sectionId?: string;
  bgClass?: string;
}

const Grid = ({ groupLinks, t, lang }: { groupLinks: IndustryLinkItem[]; t: ReturnType<typeof getServiceDetailT>; lang: ReturnType<typeof useLanguage> }) => (
        <div className={`grid gap-6 ${groupLinks.length === 1 ? 'max-w-sm' : `sm:grid-cols-2 ${groupLinks.length === 4 ? '' : 'lg:grid-cols-3'} max-w-5xl`}`}>
          {groupLinks.map((link, i) => (
            <motion.div
              key={link.title}
              className="h-full"
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
            >
              <Link
                to={localizeHref(link.href, lang)}
                className="group flex flex-col h-full bg-primary/[0.04] border border-primary/10 rounded-xl overflow-hidden hover:shadow-lg hover:border-primary/25 hover:bg-primary/[0.07] transition-all"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={link.image}
                    alt={link.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <h3 className="text-base font-bold text-foreground mb-2">
                    {link.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-4">
                    {link.description}
                  </p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-medium text-primary group-hover:gap-2 transition-all mt-auto">
                    {t.learnMore} <ArrowRight size={14} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
);

const ServiceIndustryLinks = ({ links, groupHeading, extraGroups, sectionTitle, sectionSubtitle, sectionId = "service-offerings", bgClass = "bg-secondary/50" }: Props) => {
  const lang = useLanguage();
  const t = getServiceDetailT(lang);

  return (
    <section id={sectionId} className={`py-14 sm:py-20 ${bgClass} scroll-mt-20`}>
      <div className="container mx-auto px-6 lg:px-10 max-w-6xl">
        <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
          {sectionSubtitle ?? t.ourServices}
        </p>
        <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-10">
          {sectionTitle ?? t.clinicalCROServices}
        </h2>

        {[{ heading: groupHeading, links }, ...(extraGroups ?? [])].map((group) => (
          <div key={group.heading ?? "main"} className="mb-12 last:mb-0">
            {group.heading && (
              <h3 className="text-lg sm:text-xl font-bold text-foreground mb-5">{group.heading}</h3>
            )}
            <Grid groupLinks={group.links} t={t} lang={lang} />
          </div>
        ))}
      </div>
    </section>
  );
};

export default ServiceIndustryLinks;
